import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Users, AlertTriangle, Download, UserPlus, Trash2, Phone, QrCode, Pencil, Search, X, RotateCcw } from "lucide-react";
import { ConfirmDeleteModal } from "./ConfirmDeleteModal";
import { ManualLeadModal } from "./ManualLeadModal";

const STATUT_BADGE = {
  nouveau:  { label: "À rappeler", className: "bg-[#E1ECFF] text-[#1E50C9] border-[#B9D2FF]" },
  rappele:  { label: "Rappelé",   className: "bg-[#FEF6C3] text-[#7A4A00] border-[#F4E48C]" },
  converti: { label: "Converti",  className: "bg-[#D7F8EA] text-[#0A8A57] border-[#A9F0CE]" },
  perdu:    { label: "Perdu",     className: "bg-[#E8EAEC] text-[#3C4649] border-[#BFC6CC]" },
};

const STATUT_TRANSITIONS = {
  nouveau:  [{ to: "rappele", label: "Marquer rappelé" }, { to: "perdu", label: "Perdu" }],
  rappele:  [{ to: "converti", label: "Converti" }, { to: "perdu", label: "Perdu" }],
  converti: [],
  perdu:    [{ to: "nouveau", label: "Réactiver" }],
};

const STATUT_FILTERS = [
  { value: "all", label: "Tous" },
  { value: "nouveau", label: "À rappeler" },
  { value: "rappele", label: "Rappelés" },
  { value: "converti", label: "Convertis" },
  { value: "perdu", label: "Perdus" },
];

function formatPhone(p) {
  if (!p) return "";
  return p.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
}

export function LeadsPanel() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statutFilter, setStatutFilter] = useState("all");
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [parrainOpen, setParrainOpen] = useState(null); // lead object
  const [filleuls, setFilleuls] = useState([]);
  const [filleulLoading, setFilleulLoading] = useState(false);
  const [newFilleul, setNewFilleul] = useState({ prenom: "", nom: "", telephone: "", email: "", code_postal: "" });
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [manualLeadOpen, setManualLeadOpen] = useState(false);
  const canCreateManual = user?.role === "admin" || user?.role === "licencie";
  const [submittingFilleul, setSubmittingFilleul] = useState(false);
  const [agencyFilter, setAgencyFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const isAdmin = user?.role === "admin";
  const isLicencie = user?.role === "licencie";
  const canParrainer = user?.role === "licencie" && user?.parrainage_enabled;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statutFilter !== "all") params.statut = statutFilter;
      // Admin: merchant filter is done client-side to also support "Sans commerçant" option
      if (!isAdmin && merchantFilter !== "all") params.merchant_id = merchantFilter;
      const { data } = await api.get("/leads", { params });
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [statutFilter, merchantFilter, isAdmin]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    api.get("/merchants").then(({ data }) => setMerchants(data || [])).catch(() => setMerchants([]));
  }, []);

  const transitionStatut = async (id, to) => {
    try {
      await api.patch(`/leads/${id}/statut`, { statut: to });
      toast.success("Statut mis à jour");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDeleteLead = async () => {
    if (!leadToDelete) return;
    try {
      await api.delete(`/leads/${leadToDelete.id}`);
      toast.success("Lead supprimé");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    }
  };

  const openParrain = async (lead) => {
    setParrainOpen(lead);
    setFilleulLoading(true);
    setNewFilleul({ prenom: "", nom: "", telephone: "", email: "", code_postal: lead.code_postal || "" });
    try {
      const { data } = await api.get(`/leads/${lead.id}/filleuls`);
      setFilleuls(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFilleulLoading(false);
    }
  };

  const submitFilleul = async (e) => {
    e.preventDefault();
    if (!parrainOpen) return;
    setSubmittingFilleul(true);
    try {
      await api.post("/leads/filleul", {
        prenom: newFilleul.prenom.trim(),
        nom: newFilleul.nom.trim(),
        telephone: newFilleul.telephone.trim(),
        email: newFilleul.email.trim(),
        code_postal: newFilleul.code_postal.trim(),
        parrain_id: parrainOpen.id,
      });
      toast.success("Filleul ajouté — il est désormais à rappeler.");
      const { data } = await api.get(`/leads/${parrainOpen.id}/filleuls`);
      setFilleuls(data || []);
      setNewFilleul({ prenom: "", nom: "", telephone: "", email: "", code_postal: parrainOpen.code_postal || "" });
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmittingFilleul(false);
    }
  };

  const merchantOptions = useMemo(() => {
    const opts = [{ value: "all", label: "Tous les commerçants" }];
    if (isAdmin) opts.push({ value: "none", label: "Sans commerçant (leads orphelins)" });
    return [...opts, ...merchants.map((m) => ({ value: m.id, label: m.nom }))];
  }, [merchants, isAdmin]);

  const agencyOptions = useMemo(() => {
    const set = new Set();
    items.forEach((l) => { if (l.agency_name) set.add(l.agency_name); });
    return [{ value: "all", label: "Toutes les zones" }, ...Array.from(set).sort().map((z) => ({ value: z, label: z }))];
  }, [items]);

  const filteredItems = useMemo(() => {
    let out = items;
    // Admin: apply merchant filter client-side (to support "Sans commerçant")
    if (isAdmin && merchantFilter !== "all") {
      if (merchantFilter === "none") out = out.filter((l) => !l.merchant_id);
      else out = out.filter((l) => l.merchant_id === merchantFilter);
    }
    // Zone filter — admin only
    if (isAdmin && agencyFilter !== "all") {
      out = out.filter((l) => (l.agency_name || "Siège") === agencyFilter);
    }
    // Text search — admin + licencié
    const q = query.trim().toLowerCase();
    if (q && (isAdmin || isLicencie)) {
      out = out.filter((l) => {
        const hay = `${l.prenom || ""} ${l.nom || ""} ${l.email || ""} ${l.telephone || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return out;
  }, [items, agencyFilter, merchantFilter, query, isAdmin, isLicencie]);

  const hasActiveAdminFilters = isAdmin && (
    query.trim() !== "" || merchantFilter !== "all" || agencyFilter !== "all"
  );
  const resetAdminFilters = () => {
    setQuery("");
    setMerchantFilter("all");
    setAgencyFilter("all");
  };

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [statutFilter, merchantFilter, agencyFilter, query, pageSize]);
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const pageItems = useMemo(() => filteredItems.slice((page - 1) * pageSize, page * pageSize), [filteredItems, page, pageSize]);

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(57,125,255,0.06)] p-6 sm:p-7" data-testid="leads-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#191D1E]">Leads</h2>
          <p className="text-sm text-[#939BA0] mt-0.5">Suivi des demandes de rappel.</p>
        </div>
        <Button variant="outline" onClick={reload} data-testid="leads-refresh" className="rounded-xl border-[#E5E7EB] hover:bg-[#F5F6F9]">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
        {canCreateManual && (
          <Button onClick={() => setManualLeadOpen(true)} data-testid="leads-manual-create" className="rounded-xl bg-[#1B4FA6] hover:bg-[#143C82] text-white font-semibold ml-2">
            <Phone className="w-4 h-4 mr-2" /> Nouveau lead (appel)
          </Button>
        )}
        <Button
          onClick={async () => {
            try {
              const params = new URLSearchParams();
              if (statutFilter !== "all") params.set("statut", statutFilter);
              const url = `${api.defaults.baseURL}/leads/export.csv${params.toString() ? "?" + params : ""}`;
              const token = localStorage.getItem("pde_token");
              const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
              const blob = await res.blob();
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = `leads_pde_${new Date().toISOString().slice(0,10).replace(/-/g,"")}.csv`;
              a.click();
              toast.success("Export téléchargé");
            } catch { toast.error("Échec de l'export"); }
          }}
          data-testid="leads-export-csv"
          className="bg-[#397DFF] hover:bg-[#2860D8] text-white rounded-xl ml-2"
        >
          <Download className="w-4 h-4 mr-2" /> Exporter CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="leads-filters">
        <div className="flex items-center gap-1 flex-wrap">
          {STATUT_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatutFilter(f.value)}
              data-testid={`leads-filter-${f.value}`}
              className={[
                "px-3 py-1.5 rounded-full text-xs font-semibold border transition-all",
                statutFilter === f.value
                  ? "bg-[#397DFF] text-white border-[#397DFF]"
                  : "bg-white text-[#687175] border-[#E5E7EB] hover:bg-[#F5F6F9]",
              ].join(" ")}
            >
              {f.label}
            </button>
          ))}
        </div>
        {isAdmin && (
          <div className="ml-auto flex gap-2 flex-wrap">
            <div className="min-w-[200px]">
              <Select value={agencyFilter} onValueChange={setAgencyFilter}>
                <SelectTrigger data-testid="admin-leads-filter-zone"><SelectValue /></SelectTrigger>
                <SelectContent>{agencyOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="min-w-[240px]">
              <Select value={merchantFilter} onValueChange={setMerchantFilter}>
                <SelectTrigger data-testid="admin-leads-filter-merchant"><SelectValue /></SelectTrigger>
                <SelectContent>{merchantOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Search bar (admin + licencié) */}
      {(isAdmin || isLicencie) && (
        <>
          <div className="relative mb-3">
            <Search className="w-4 h-4 text-[#939BA0] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un client par nom, prénom, email ou téléphone…"
              data-testid={isAdmin ? "admin-leads-search-input" : "leads-search-input"}
              className="pl-9 pr-9 h-10 rounded-xl border-[#E5E7EB] focus-visible:ring-[#1B4FA6]/40 focus-visible:border-[#1B4FA6]"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                data-testid={isAdmin ? "admin-leads-search-clear" : "leads-search-clear"}
                aria-label="Effacer la recherche"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-[#687175] hover:text-[#1F2937] hover:bg-[#F5F6F9] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-xs text-[#687175]" data-testid={isAdmin ? "admin-leads-count" : "leads-count"}>
              {(query.trim() || (isAdmin && (merchantFilter !== "all" || agencyFilter !== "all")))
                ? `${filteredItems.length} / ${items.length} leads affichés`
                : `${items.length} lead${items.length > 1 ? "s" : ""} affiché${items.length > 1 ? "s" : ""}`}
            </div>
            {hasActiveAdminFilters && (
              <button
                type="button"
                onClick={resetAdminFilters}
                data-testid="admin-leads-reset-filters"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-[#1B4FA6] border border-[#1B4FA6]/25 bg-[#EEF2FF] hover:bg-[#DDE7FF] transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Réinitialiser les filtres
              </button>
            )}
          </div>
        </>
      )}

      {items.length === 0 && !loading && (
        <div className="py-12 text-center text-sm text-[#939BA0]" data-testid="leads-empty">
          <Users className="w-8 h-8 mx-auto mb-3 text-[#BFC6CC]" />
          Aucun lead avec ce filtre.
        </div>
      )}
      {items.length > 0 && filteredItems.length === 0 && !loading && (
        <div className="py-12 text-center text-sm text-[#939BA0]" data-testid="admin-leads-search-empty">
          <Search className="w-8 h-8 mx-auto mb-3 text-[#BFC6CC]" />
          Aucun lead ne correspond à ces filtres.
        </div>
      )}

      {filteredItems.length > 0 && (
        <div className="overflow-x-auto -mx-2 max-h-[calc(100vh-340px)] overflow-y-auto">
          <table className="w-full text-[13px]" data-testid="leads-table">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#939BA0] border-b border-[#E5E7EB]">
                <th className="px-2 py-2">Date</th>
                <th className="px-2 py-2">Client</th>
                <th className="px-2 py-2">Contact</th>
                <th className="px-2 py-2">CP</th>
                <th className="px-2 py-2">Commerçant</th>
                {isAdmin && <th className="px-2 py-2">Agence</th>}
                <th className="px-2 py-2">Statut</th>
                <th className="px-2 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((l) => {
                const badge = STATUT_BADGE[l.statut] || STATUT_BADGE.nouveau;
                const transitions = STATUT_TRANSITIONS[l.statut] || [];
                return (
                  <tr key={l.id} className="border-b border-[#EEF0F4] last:border-0 hover:bg-[#FAFBFC]" data-testid={`lead-row-${l.id}`}>
                    <td className="px-2 py-2 text-xs text-[#939BA0] whitespace-nowrap">{new Date(l.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="px-2 py-2">
                      <div className="font-semibold text-[#191D1E] flex items-center gap-2">
                        {l.prenom} {l.nom}
                        {l.parrain_id && (
                          <Badge data-testid={`lead-filleul-${l.id}`} className="bg-[#FCE7F3] text-[#9D174D] border border-[#FBCFE8] text-[10px] px-1.5 py-0 whitespace-nowrap">Filleul</Badge>
                        )}
                        {l.is_duplicate && (
                          <Badge data-testid={`lead-dup-${l.id}`} className="bg-[#FFF7E6] text-[#92400E] border border-[#FDE68A] gap-1">
                            <AlertTriangle className="w-3 h-3" /> Doublon
                          </Badge>
                        )}
                        {l.source === "seed" && (
                          <Badge className="bg-[#EEF2FF] text-[#3730A3] border border-[#C7D2FE]">Démo</Badge>
                        )}
                      </div>
                      <div className="text-xs text-[#939BA0]">{l.email}</div>
                    </td>
                    <td className="px-2 py-2 text-[#687175] whitespace-nowrap">{formatPhone(l.telephone)}</td>
                    <td className="px-2 py-2 text-[#687175]">{l.code_postal}</td>
                    <td className="px-2 py-2 text-[#687175] truncate max-w-[160px]" title={l.merchant_name || ""}>{l.merchant_name || <span className="text-[#BFC6CC]">—</span>}</td>
                    {isAdmin && (
                      <td className="px-2 py-2 text-[#687175] whitespace-nowrap truncate max-w-[140px]" title={l.agency_name || "Siège"}>
                        {l.agency_name || <span className="text-[#BFC6CC]">Siège</span>}
                      </td>
                    )}
                    <td className="px-2 py-2"><Badge className={`${badge.className} border whitespace-nowrap text-[11px] px-2 py-0.5`}>{badge.label}</Badge></td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      {l.statut === "converti" && canParrainer && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openParrain(l)}
                          data-testid={`lead-${l.id}-parrain`}
                          className="rounded-lg mr-1 text-xs border-[#F9C80E] text-[#7A4A00] hover:bg-[rgba(249,200,14,0.1)]"
                        >
                          <UserPlus className="w-3.5 h-3.5 mr-1" /> Filleuls
                        </Button>
                      )}
                      {transitions.map((t) => (
                        <Button
                          key={t.to}
                          size="sm"
                          variant="outline"
                          onClick={() => transitionStatut(l.id, t.to)}
                          data-testid={`lead-${l.id}-to-${t.to}`}
                          className="rounded-lg mr-1 text-xs"
                        >
                          {t.label}
                        </Button>
                      ))}
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setLeadToDelete(l)}
                          data-testid={`lead-${l.id}-delete`}
                          className="rounded-lg text-[#B91C1C] border-[#FECACA] hover:bg-[#FEF2F2]"
                          title="Supprimer définitivement ce lead"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {filteredItems.length > 0 && (
        <div className="mt-3 flex items-center justify-between flex-wrap gap-3 text-xs text-[#687175]" data-testid="leads-pagination">
          <div>
            Affichage <strong className="text-[#191D1E]">{(page - 1) * pageSize + 1}</strong>–<strong className="text-[#191D1E]">{Math.min(page * pageSize, filteredItems.length)}</strong> sur <strong className="text-[#191D1E]">{filteredItems.length}</strong> leads
          </div>
          <div className="flex items-center gap-2">
            <span>Par page&nbsp;:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              data-testid="leads-page-size"
              className="rounded-lg border border-[#E5E7EB] bg-white px-2 py-1 text-[#191D1E] hover:border-[#397DFF] focus:outline-none focus:ring-2 focus:ring-[#397DFF]/20"
            >
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <Button
              size="sm" variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="leads-page-prev"
              className="rounded-lg border-[#E5E7EB] h-8 px-3"
            >‹ Précédent</Button>
            <span className="px-2"><strong className="text-[#191D1E]">{page}</strong> / {totalPages}</span>
            <Button
              size="sm" variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="leads-page-next"
              className="rounded-lg border-[#E5E7EB] h-8 px-3"
            >Suivant ›</Button>
          </div>
        </div>
      )}


      <Dialog open={!!parrainOpen} onOpenChange={(v) => !v && setParrainOpen(null)}>
        <DialogContent data-testid="parrain-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Filleuls de {parrainOpen?.prenom} {parrainOpen?.nom}</DialogTitle>
            <DialogDescription>
              Pour chaque filleul converti, <strong>+{user?.parrainage_amount || 10} € TTC</strong> seront crédités à la cagnotte du parrain chez son commerçant. Financé par votre agence.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-[#939BA0] mb-2">Filleuls existants ({filleuls.length})</div>
              {filleulLoading ? (
                <div className="text-sm text-[#939BA0] py-3">Chargement…</div>
              ) : filleuls.length === 0 ? (
                <div className="text-sm text-[#939BA0] py-3 italic">Aucun filleul pour le moment.</div>
              ) : (
                <div className="space-y-1.5">
                  {filleuls.map((f) => {
                    const b = STATUT_BADGE[f.statut] || STATUT_BADGE.nouveau;
                    return (
                      <div key={f.id} className="flex items-center justify-between p-2 rounded-lg bg-[#F5F6F9] border border-[#E5E7EB]" data-testid={`filleul-${f.id}`}>
                        <div>
                          <div className="text-sm font-semibold text-[#191D1E]">{f.prenom} {f.nom}</div>
                          <div className="text-xs text-[#687175]">{formatPhone(f.telephone)} · {f.email}</div>
                        </div>
                        <Badge className={`${b.className} border`}>{b.label}</Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <form onSubmit={submitFilleul} className="space-y-3 pt-4 border-t border-[#E5E7EB]" data-testid="filleul-form">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#939BA0]">Ajouter un filleul</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">Prénom</Label><Input value={newFilleul.prenom} onChange={(e) => setNewFilleul((f) => ({...f, prenom: e.target.value}))} required className="rounded-lg" data-testid="filleul-prenom" /></div>
                <div><Label className="text-xs font-semibold">Nom</Label><Input value={newFilleul.nom} onChange={(e) => setNewFilleul((f) => ({...f, nom: e.target.value}))} required className="rounded-lg" data-testid="filleul-nom" /></div>
                <div><Label className="text-xs font-semibold">Téléphone</Label><Input inputMode="tel" value={newFilleul.telephone} onChange={(e) => setNewFilleul((f) => ({...f, telephone: e.target.value}))} required className="rounded-lg" data-testid="filleul-tel" /></div>
                <div><Label className="text-xs font-semibold">Code postal</Label><Input inputMode="numeric" maxLength={5} value={newFilleul.code_postal} onChange={(e) => setNewFilleul((f) => ({...f, code_postal: e.target.value}))} required className="rounded-lg" data-testid="filleul-cp" /></div>
                <div className="col-span-2"><Label className="text-xs font-semibold">Email</Label><Input type="email" value={newFilleul.email} onChange={(e) => setNewFilleul((f) => ({...f, email: e.target.value}))} required className="rounded-lg" data-testid="filleul-email" /></div>
              </div>
              <Button type="submit" disabled={submittingFilleul} className="bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl w-full" data-testid="filleul-submit">
                {submittingFilleul ? "Enregistrement…" : "Ajouter ce filleul"}
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteModal
        open={!!leadToDelete}
        onOpenChange={(v) => !v && setLeadToDelete(null)}
        entityType="lead / client"
        entityLabel={leadToDelete?.email || `${leadToDelete?.prenom || ""} ${leadToDelete?.nom || ""}`.trim()}
        description={`Vous êtes sur le point de supprimer définitivement le lead ${leadToDelete?.prenom || ""} ${leadToDelete?.nom || ""}.`}
        consequences={[
          "Le lead et toutes ses informations sont supprimés de la base.",
          "Aucun email n'est envoyé au client.",
          "Cette action est irréversible.",
        ]}
        onConfirm={onDeleteLead}
      />

      <ManualLeadModal
        open={manualLeadOpen}
        onOpenChange={setManualLeadOpen}
        onCreated={() => reload()}
      />
    </div>
  );
}

export default LeadsPanel;
